/* ====================================================================================================================
   PEN'UP — MENU (moteur)

   Deux éléments personnalisés, sans dépendance au moteur de menu d'Impact :

     <penup-nav>     navigation « Lunii » — ordinateur / tablette
     <penup-drawer>  tiroir « Koh »       — téléphone

   Pourquoi ne pas réutiliser <details is="mega-menu-disclosure"> d'Impact : son moteur ouvre
   instantanément et ferme en 500ms, sans notion de panneau PARTAGÉ. Or toute la douceur de Lunii
   tient à ça — une seule carte qui se redimensionne et dont le contenu se refond, plus un
   soulignement qui glisse d'une entrée à l'autre. Mesures : audit/lunii-hover.json.

     · ouverture depuis l'état fermé : 200ms de latence, puis scaleIn 200ms
       (rotateX -30deg + scale .9 → 0/1, perspective 2000px, origine haut-centre)
     · changement d'entrée, panneau déjà ouvert : la BOÎTE se recale d'un coup,
       seul le contenu rejoue scaleAndOpacity 250ms — aucune latence
     · fermeture : 150ms de latence, puis scaleOut 200ms
     · indicateur souligné : translateX + width en 300ms ease-out
   ==================================================================================================================== */

(function () {
  'use strict';

  const FINE_POINTER = () => window.matchMedia('(pointer: fine)').matches;
  const num = (el, prop, fallback) => {
    const raw = getComputedStyle(el).getPropertyValue(prop).trim();
    if (!raw) return fallback;
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return fallback;
    return raw.endsWith('ms') ? value : value * (raw.endsWith('s') ? 1000 : 1);
  };

  /* ==================================================================================================================
     1. NAVIGATION LUNII
     ================================================================================================================== */
  class PenupNav extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-pum-bar]');
      this.indicator = this.querySelector('[data-pum-indicator]');
      this.viewport = this.querySelector('[data-pum-viewport]');
      if (!this.bar || !this.viewport) return;

      this.panel = this.viewport.querySelector('[data-pum-panel]');
      this.triggers = Array.from(this.querySelectorAll('[data-pum-trigger]'));
      this.contents = Array.from(this.viewport.querySelectorAll('[data-pum-content]'));
      this.openKey = null;
      this.openTimer = null;
      this.closeTimer = null;

      // Le panneau est en position fixed : un ancêtre transformé (l'en-tête d'Impact porte
      // `will-change: transform`) en ferait son bloc conteneur. On le déplace donc dans <body>.
      this._viewportHome = this.viewport.parentNode;
      document.body.appendChild(this.viewport);

      this._onPointerEnterTrigger = this._onPointerEnterTrigger.bind(this);
      this._onPointerLeaveZone = this._onPointerLeaveZone.bind(this);
      this._onPointerEnterZone = this._onPointerEnterZone.bind(this);
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._reflow = this._reflow.bind(this);

      // Les écoutes valent pour TOUTES les entrées, y compris les liens simples : c'est ce qui
      // fait glisser le soulignement sur « Recharges de fil », « Modèles » et « Avis ».
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('pointerenter', this._onPointerEnterTrigger);
        trigger.addEventListener('click', this._onTriggerClick);
        trigger.addEventListener('focus', this._onPointerEnterTrigger);
      });

      this.addEventListener('pointerleave', this._onPointerLeaveZone);
      this.addEventListener('pointerenter', this._onPointerEnterZone);
      this.viewport.addEventListener('pointerleave', this._onPointerLeaveZone);
      this.viewport.addEventListener('pointerenter', this._onPointerEnterZone);
      document.addEventListener('keydown', this._onKeydown);
      this.addEventListener('focusout', this._onFocusOut);
      window.addEventListener('resize', this._reflow, { passive: true });
      window.addEventListener('scroll', this._reflow, { passive: true });

      // Les images du panneau arrivent après coup : la taille mesurée doit suivre.
      if ('ResizeObserver' in window) {
        this._observer = new ResizeObserver(() => {
          if (this.openKey) this._sizeToContent(this.openKey, false);
        });
        this.contents.forEach((content) => this._observer.observe(content));
      }

      this._reflow();
    }

    disconnectedCallback() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('resize', this._reflow);
      window.removeEventListener('scroll', this._reflow);
      if (this._observer) this._observer.disconnect();
      this._unlockHeader();
      if (this.viewport && this.viewport.parentNode === document.body) this.viewport.remove();
    }

    /* ----------------------------------------------------------------------------- géométrie */

    get header() {
      return this._header || (this._header = this.closest('.shopify-section--header') || this.closest('header'));
    }

    /** Ancre le panneau sous l'en-tête et publie la largeur de l'ascenseur (l'unité de grille en dépend). */
    _anchor() {
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      this.style.setProperty('--pum-scrollbar', `${scrollbar}px`);
      this.viewport.style.setProperty('--pum-scrollbar', `${scrollbar}px`);

      const anchor = this.header ? Math.max(0, Math.round(this.header.getBoundingClientRect().bottom)) : 64;
      this.viewport.style.setProperty('--pum-anchor', `${anchor}px`);
    }

    /* Recalage sur redimensionnement / défilement UNIQUEMENT : ici l'indicateur doit se
       téléporter (la barre a bougé sous lui), alors qu'à l'ouverture il doit GLISSER. */
    _reflow() {
      this._anchor();
      if (this.openKey) {
        this._sizeToContent(this.openKey, false);
        this._moveIndicator(this.openKey, false);
      }
    }

    _sizeToContent(key, animate) {
      const content = this.contents.find((c) => c.dataset.pumContent === key);
      if (!content || !this.panel) return;
      // La cellule est en position absolue et en largeur automatique : offsetWidth donne
      // sa largeur naturelle, exactement ce que Radix mesure chez Lunii.
      const width = content.offsetWidth;
      const height = content.offsetHeight;
      if (!width || !height) return;
      this.panel.style.setProperty('--pum-w', `${width}px`);
      this.panel.style.setProperty('--pum-h', `${height}px`);
      this._placePanel(key, width);
      if (!animate) return;
    }

    /**
     * Décale le panneau pour qu'il reste SOUS son entrée.
     *
     * Lunii centre son panneau sur la fenêtre, toujours — mesuré sur les quatre déroulants
     * (`tools/lunii-panneau.mjs`). Mais leurs panneaux font 741 à 918 px : à cette largeur, un
     * panneau centré couvre forcément l'entrée qui l'a ouvert. Un panneau étroit — une seule
     * carte — ne la couvre plus, et la fiche paraît flotter à côté de son entrée.
     *
     * D'où les trois modes du réglage « Position du panneau » :
     *   auto     la loi Lunii, sauf quand le panneau ne couvre plus son entrée (défaut)
     *   center   la loi Lunii, sans exception
     *   trigger  toujours centré sous l'entrée
     * Dans tous les cas le panneau reste dans la fenêtre, marge comprise.
     */
    _placePanel(key, width) {
      const trigger = this.triggers.find((t) => t.dataset.pumKey === key);
      if (!trigger) return;

      const mode = this.viewport.dataset.pumAlign || 'auto';
      const inset = num(this.panel, '--pum-viewport-inset', 32);
      const vw = this.viewport.getBoundingClientRect().width;
      const w = Math.min(width, vw - inset * 2);

      const centre = (vw - w) / 2;                       // ce que fait Lunii
      const box = trigger.getBoundingClientRect();
      const sous = Math.min(Math.max(box.left + box.width / 2 - w / 2, inset), vw - inset - w);

      const couvre = centre <= box.left && centre + w >= box.right;
      const x = mode === 'center' || (mode === 'auto' && couvre) ? centre : sous;

      this.panel.style.setProperty('--pum-panel-offset', `${Math.round(x - centre)}px`);
    }

    /**
     * Déplace le « fil d'Ariane ».
     * @param target  un déclencheur, ou la clé d'une entrée à panneau
     * @param animate true = il GLISSE ; false = il se pose sans traverser la barre
     *
     * Il suit le survol de TOUTES les entrées, y compris les liens simples sans panneau
     * (« Recharges de fil », « Modèles », « Avis ») — vérifié sur lunii.com : leur indicateur
     * est bien visible et positionné quand on survole « Carte cadeau », qui n'a aucun panneau.
     */
    _moveIndicator(target, animate) {
      if (!this.indicator) return;
      const trigger =
        target instanceof HTMLElement ? target : this.triggers.find((t) => t.dataset.pumKey === target);
      if (!trigger) {
        this.indicator.dataset.visible = 'false';
        return;
      }
      const barBox = this.bar.getBoundingClientRect();
      const box = trigger.getBoundingClientRect();
      this.indicator.dataset.teleport = animate ? 'false' : 'true';
      this.indicator.style.width = `${box.width}px`;
      this.indicator.style.transform = `translateX(${box.left - barBox.left}px)`;
      this.indicator.dataset.visible = 'true';
      if (!animate) {
        // On laisse le glissement reprendre la main au prochain changement.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.indicator.dataset.teleport = 'false';
          });
        });
      }
    }

    /* ------------------------------------------------------------------------------- ouverture */

    open(key) {
      clearTimeout(this.closeTimer);
      if (this.openKey === key) return;

      const wasOpen = this.openKey !== null;
      this.openKey = key;

      this.contents.forEach((content) => {
        const active = content.dataset.pumContent === key;
        content.dataset.active = active ? 'true' : 'false';
        content.toggleAttribute('inert', !active);
      });

      this.triggers.forEach((trigger) => {
        if (!trigger.dataset.pumKey) return;
        trigger.setAttribute('aria-expanded', trigger.dataset.pumKey === key ? 'true' : 'false');
      });

      // Un changement d'entrée ne rejoue PAS l'ouverture de la boîte (mesuré chez Lunii :
      // la boîte se recale d'un coup, seul le contenu se refond).
      this.viewport.dataset.switching = wasOpen ? 'true' : 'false';
      this.viewport.dataset.state = 'open';
      this.viewport.setAttribute('aria-hidden', 'false');
      this._lockHeader();

      this._anchor();
      this._sizeToContent(key, true);
      // Panneau déjà ouvert → le soulignement GLISSE ; ouverture à froid → il se pose.
      this._moveIndicator(key, wasOpen);

      // Rejoue l'animation de contenu même si l'élément était déjà « actif ».
      const content = this.contents.find((c) => c.dataset.pumContent === key);
      if (content) {
        content.style.animation = 'none';
        void content.offsetHeight;
        content.style.animation = '';
      }
    }

    close(immediate) {
      clearTimeout(this.openTimer);
      if (this.openKey === null) return;
      this.openKey = null;

      this.triggers.forEach((trigger) => {
        if (trigger.dataset.pumKey) trigger.setAttribute('aria-expanded', 'false');
      });
      // La fermeture ne doit PAS effacer le soulignement si le pointeur est déjà sur une autre
      // entrée : en passant d'un déroulant à un lien simple voisin (« Boutique » → « Recharges de
      // fil »), le trait s'y posait puis disparaissait 150 ms plus tard, quand la fermeture
      // différée du panneau se déclenchait.
      if (this.indicator) {
        const hovered = this.querySelector('[data-pum-trigger]:hover');
        if (hovered) this._moveIndicator(hovered, true);
        else this.indicator.dataset.visible = 'false';
      }
      this._unlockHeader();

      if (immediate) {
        this._finishClose();
        return;
      }

      // Sans ça, `[data-switching] .pum-panel { animation: none }` — posé pour qu'un changement
      // d'entrée ne rejoue pas l'ouverture — priverait AUSSI la fermeture de son animation.
      this.viewport.dataset.switching = 'false';
      this.viewport.dataset.state = 'closing';
      const duration = num(this.panel || this.viewport, '--pum-close-duration', 200);
      clearTimeout(this._closeAnimTimer);
      this._closeAnimTimer = setTimeout(() => this._finishClose(), duration);
    }

    _finishClose() {
      clearTimeout(this._closeAnimTimer);
      this.viewport.dataset.state = 'closed';
      this.viewport.dataset.switching = 'false';
      this.viewport.setAttribute('aria-hidden', 'true');
      this.contents.forEach((content) => {
        content.dataset.active = 'false';
        content.toggleAttribute('inert', true);
      });
    }

    /* Chez Lunii, l'en-tête devient opaque tant qu'un panneau est ouvert (utile en en-tête transparent). */
    _lockHeader() {
      if (this.header) this.header.setAttribute('data-pum-open', 'true');
    }

    _unlockHeader() {
      if (this.header) this.header.removeAttribute('data-pum-open');
    }

    /* -------------------------------------------------------------------------------- écoutes */

    _onPointerEnterTrigger(event) {
      const trigger = event.currentTarget;
      const key = trigger.dataset.pumKey;
      const pointing = event.type === 'pointerenter';

      if (pointing && !FINE_POINTER()) return;

      // Le soulignement suit le survol, panneau ou pas.
      if (pointing || event.type === 'focus') {
        this._moveIndicator(trigger, this.indicator && this.indicator.dataset.visible === 'true');
      }

      if (!key) {
        // Une entrée sans panneau ferme celui qui serait ouvert (comportement Radix).
        this._scheduleClose();
        return;
      }
      if (event.type === 'focus' && !this.openKey) return; // le focus seul n'ouvre pas

      clearTimeout(this.closeTimer);
      if (this.openKey !== null) {
        this.open(key); // déjà ouvert : bascule immédiate
        return;
      }
      clearTimeout(this.openTimer);
      const delay = num(this, '--pum-open-delay', 200);
      this.openTimer = setTimeout(() => this.open(key), delay);
    }

    _onPointerEnterZone() {
      clearTimeout(this.closeTimer);
    }

    _onPointerLeaveZone(event) {
      this._scheduleClose();

      // En quittant la barre : si un panneau est ouvert le trait revient sur SON entrée
      // (on descend dans le panneau) ; sinon il s'efface.
      if (event.currentTarget === this && this.indicator) {
        if (this.openKey) this._moveIndicator(this.openKey, true);
        else this.indicator.dataset.visible = 'false';
      }
    }

    _scheduleClose() {
      clearTimeout(this.openTimer);
      const delay = num(this, '--pum-close-delay', 150);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.close(false), delay);
    }

    _onTriggerClick(event) {
      const trigger = event.currentTarget;
      const key = trigger.dataset.pumKey;
      if (!key) return; // lien simple : on laisse filer

      const url = trigger.dataset.pumUrl;
      // Au pointeur fin, un clic sur une entrée déjà ouverte suit son lien si elle en a un.
      if (FINE_POINTER() && this.openKey === key && url) {
        window.location.href = url;
        return;
      }
      event.preventDefault();
      if (this.openKey === key) this.close(false);
      else this.open(key);
    }

    _onKeydown(event) {
      if (event.key !== 'Escape' || this.openKey === null) return;
      const trigger = this.triggers.find((t) => t.dataset.pumKey === this.openKey);
      this.close(true);
      if (trigger) trigger.focus();
    }

    _onFocusOut(event) {
      if (this.openKey === null) return;
      const next = event.relatedTarget;
      if (!next) return;
      if (this.contains(next) || (this.viewport && this.viewport.contains(next))) return;
      this.close(true);
    }
  }

  /* ==================================================================================================================
     2. TIROIR KOH
     ================================================================================================================== */
  class PenupDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-pum-drawer-panel]');
      this.overlay = this.querySelector('[data-pum-drawer-overlay]');
      this.closeButton = this.querySelector('[data-pum-drawer-close]');
      if (!this.panel) return;

      this._nodes = [this.overlay, this.closeButton, this.panel].filter(Boolean);
      this._home = this.parentNode;
      // Même raison que pour le panneau : sortir du bloc conteneur de l'en-tête.
      this._nodes.forEach((node) => document.body.appendChild(node));

      this.dataset.state = this.dataset.state || 'closed';
      this.panel.setAttribute('inert', '');
      this._onToggle = this._onToggle.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._toggle = this._toggle.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._reflow = this._reflow.bind(this);
      this._warm = this._warm.bind(this);

      this._toggles = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
      this._toggles.forEach((btn) => {
        // Le tiroir s'ouvre au CONTACT du doigt, pas au clic. Un `click` n'arrive qu'au
        // relâchement : mesuré à 118 ms après le toucher pour un appui ordinaire. C'est
        // cette attente, et elle seule, qui donnait l'impression de latence.
        btn.addEventListener('pointerdown', this._onPointerDown);
        // Repli clavier (Entrée / Espace sur le bouton) et navigateurs sans PointerEvent.
        btn.addEventListener('click', this._onToggle);
        btn.setAttribute('aria-expanded', 'false');
      });

      if (this.closeButton) this.closeButton.addEventListener('click', () => this.close());
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', this._onKeydown);
      window.addEventListener('resize', this._reflow, { passive: true });
      window.addEventListener('orientationchange', this._reflow, { passive: true });
      window.addEventListener('scroll', this._onScroll, { passive: true });

      // Un autre tiroir du thème (panier, recherche) qui s'ouvre pendant que le menu est
      // ouvert : on referme le menu. Sinon les deux se superposent (même z-index 999) et,
      // le panneau du menu étant ajouté à <body> après le tiroir panier, le panier passait
      // DESSOUS le menu sur téléphone (constat 2026-09-03).
      this._onForeignDrawer = this._onForeignDrawer.bind(this);
      document.addEventListener('pointerdown', this._onForeignDrawer, true);
      document.addEventListener('click', this._onForeignDrawer, true);
      this._foreignObserver = new MutationObserver((records) => {
        if (this.dataset.state !== 'open') return;
        for (const record of records) {
          const el = record.target;
          if (!el.matches || !el.matches(PenupDrawer.FOREIGN)) continue;
          if (el.hasAttribute('open') || el.getAttribute('aria-hidden') === 'false') {
            this.close();
            return;
          }
        }
      });
      this._foreignObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open', 'aria-hidden'] });

      this._reflow();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('pointerdown', this._onForeignDrawer, true);
      document.removeEventListener('click', this._onForeignDrawer, true);
      if (this._foreignObserver) this._foreignObserver.disconnect();
      window.removeEventListener('resize', this._reflow);
      window.removeEventListener('orientationchange', this._reflow);
      window.removeEventListener('scroll', this._onScroll);
      cancelAnimationFrame(this._scrollFrame);
      this._nodes.forEach((node) => node.remove());
    }

    get header() {
      return this._header || (this._header = document.querySelector('.shopify-section--header'));
    }

    /**
     * Le tiroir démarre SOUS la barre d'annonce et l'en-tête, il ne les recouvre jamais.
     * (Koh code 93px en dur alors que sa propre barre mesure 97 ; on calcule la valeur exacte.)
     *
     * Cette lecture provoque un calcul de mise en page : elle est donc faite À L'AVANCE —
     * au chargement, au redimensionnement, et au fil du défilement — jamais au moment du
     * geste. `open()` ne fait plus qu'écrire des attributs.
     */
    _reflow() {
      if (!this.header) return;
      const top = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
      if (top === this._top) return;
      this._top = top;
      this._nodes.forEach((node) => node.style.setProperty('--pum-drawer-top', `${top}px`));
    }

    /** Recalage en marge du défilement, une fois par image au plus. */
    _onScroll() {
      if (this._scrollFrame) return;
      this._scrollFrame = requestAnimationFrame(() => {
        this._scrollFrame = 0;
        this._reflow();
      });
    }

    /**
     * Réchauffage à l'INTENTION (doigt posé sur le bouton), une seule fois :
     *   · les vignettes encore paresseuses passent en chargement immédiat ;
     *   · le tiroir est promu en couche de composition, pour que le compositeur l'ait
     *     déjà tramé quand le doigt se relève.
     * Aucun `decode()` forcé : sur un téléphone modeste, décoder huit images d'un coup
     * occupe le fil principal au pire moment — mesuré à 75 ms. Le tramage se fait tout
     * seul, du bon côté, entre l'appui et le relâchement.
     */
    _warm() {
      if (this._warmed) return;
      this._warmed = true;
      this.panel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
      // ⚠️ NE PAS rendre le tiroir visible ici pour le pré-tramer : décalé de -100 %, sa bande
      // basse retombe sur la barre d'en-tête, et comme il est au plan 999 contre 10 pour
      // l'en-tête, il RECOUVRE le bouton. Le clic part alors dans le tiroir et le menu ne
      // s'ouvre plus. Le tramage anticipé ne valait de toute façon qu'une image sur un
      // processeur bridé ×4 : sur un téléphone normal, l'ouverture est déjà au niveau du
      // témoin (8,3 ms de moyenne, avec ou sans).
      this.panel.style.willChange = 'transform';
    }

    /** Contact du doigt (ou bouton gauche de la souris) : on bascule tout de suite. */
    _onPointerDown(event) {
      if (event.button && event.button !== 0) return;
      this._pointerAt = performance.now();
      this._toggle();
    }

    /**
     * Le `click` qui suit notre `pointerdown` refermerait aussitôt le tiroir : on l'ignore.
     * En revanche un `click` SANS pointerdown récent vient du clavier — là, on bascule.
     */
    _onToggle(event) {
      event.preventDefault();
      if (this._pointerAt && performance.now() - this._pointerAt < 700) return;
      this._toggle();
    }

    _toggle() {
      if (this.dataset.state === 'open') this.close();
      else this.open();
    }

    open() {
      clearTimeout(this._timer);
      this.dataset.state = 'open';
      this._nodes.forEach((node) => (node.dataset.state = 'open'));
      this._toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      // L'en-tête passe AU-DESSUS du voile : sans ça le bouton du menu, devenu croix,
      // serait recouvert et donc incliquable.
      if (this.header) this.header.setAttribute('data-pum-drawer', 'open');
      this.panel.removeAttribute('inert');

      // Tout ce qui n'est pas indispensable à la première image passe après elle.
      requestAnimationFrame(() => {
        this._warm();
        const focusable = this.panel.querySelector('a, button');
        if (focusable && FINE_POINTER()) focusable.focus({ preventScroll: true });
      });
    }

    close() {
      if (this.dataset.state === 'closed') return;
      this.dataset.state = 'closing';
      this._nodes.forEach((node) => (node.dataset.state = 'closing'));
      this._toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      const duration = num(this.panel, '--pum-drawer-close', 200);
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.dataset.state = 'closed';
        this._nodes.forEach((node) => (node.dataset.state = 'closed'));
        if (this.header) this.header.removeAttribute('data-pum-drawer');
        // On rend la mémoire : ni couche de composition ni surface tramée tant qu'il dort.
        this.panel.style.willChange = '';
        this.panel.setAttribute('inert', '');
        this._warmed = false;
      }, duration);
    }

    /* Aucun verrou en JavaScript : `overflow: hidden` sur <html> recalcule la mise en page
       ENTIÈRE au pire moment. Le blocage est purement CSS — `touch-action: none` sur le voile
       et `overscroll-behavior: contain` sur le corps du tiroir. Coût : zéro. */

    _onKeydown(event) {
      if (event.key === 'Escape' && this.dataset.state === 'open') this.close();
    }

    /* Déclencheur d'un autre tiroir (icône panier, loupe…) touché pendant que le menu est ouvert. */
    _onForeignDrawer(event) {
      if (this.dataset.state !== 'open') return;
      const trigger = event.target && event.target.closest ? event.target.closest('[aria-controls]') : null;
      if (!trigger) return;
      const id = trigger.getAttribute('aria-controls');
      if (!id || id === this.id) return;
      const target = document.getElementById(id);
      if (target && target.matches(PenupDrawer.FOREIGN)) this.close();
    }
  }
  PenupDrawer.FOREIGN = '.drawer, cart-drawer, search-drawer';

  if (!customElements.get('penup-nav')) customElements.define('penup-nav', PenupNav);
  if (!customElements.get('penup-drawer')) customElements.define('penup-drawer', PenupDrawer);
})();
